import { useState, useEffect } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { dashboard, commissions, messages } from '@/lib/api';
import { formatCurrency, formatCompactCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DollarSign,
  TrendingUp,
  Users,
  Clock,
  Building2,
  Award,
  Check,
  CheckCircle2,
  UserCircle,
  Receipt,
  MessageSquare,
  Send,
  Trash2,
  Plus,
  Bell,
  Home,
  Briefcase,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  X,
  Printer
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6', '#EF4444', '#14B8A6', '#F97316', '#84CC16', '#0EA5E9', '#A855F7'];

export default function Dashboard() {
  const { user } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const isAgent = user?.role === 'agent';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  
  // Year selector state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);
  
  // Message board state
  const [messageList, setMessageList] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [newMessage, setNewMessage] = useState({ title: '', content: '', send_email: true });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Top agents expansion state
  const [showAllAgents, setShowAllAgents] = useState(false);
  
  // Agent commission breakdown modal state
  const [selectedAgentForBreakdown, setSelectedAgentForBreakdown] = useState(null);
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [expandedStatementDates, setExpandedStatementDates] = useState({});

  // Business unit labels
  const buLabel = businessUnit === 'insurance' ? 'Insurance & Investment' : 'Mortgage';
  const BuIcon = businessUnit === 'insurance' ? Briefcase : Home;

  useEffect(() => {
    loadStats();
    loadMessages();
  }, [businessUnit, selectedYear]); // Reload when business unit or year changes

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await dashboard.stats(selectedYear);
      setStats(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const loadPendingPayments = async () => {
    setLoadingPending(true);
    try {
      const response = await commissions.list();
      // Filter for pending payments (no payment_date)
      const pending = response.data.filter(c => !c.payment_date || c.payment_date === '');
      setPendingPayments(pending);
    } catch (err) {
      toast.error('Failed to load pending payments');
    } finally {
      setLoadingPending(false);
    }
  };

  const handleOpenPendingModal = () => {
    setShowPendingModal(true);
    loadPendingPayments();
  };

  const handleMarkAsPaid = async (commission) => {
    setMarkingPaid(commission.id);
    try {
      const today = new Date().toISOString().split('T')[0];
      await commissions.update(commission.id, {
        ...commission,
        payment_date: today
      });
      toast.success(`Payment marked as paid for ${commission.agent_name}`);
      // Reload pending payments and stats
      await Promise.all([loadPendingPayments(), loadStats()]);
    } catch (err) {
      toast.error('Failed to mark payment as paid');
    } finally {
      setMarkingPaid(null);
    }
  };

  // Message board functions
  const loadMessages = async () => {
    try {
      const response = await messages.list(businessUnit);
      setMessageList(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    }
  };

  const handlePostMessage = async () => {
    if (!newMessage.title.trim() || !newMessage.content.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }
    
    setSendingMessage(true);
    try {
      await messages.create({ ...newMessage, business_unit: businessUnit });
      toast.success('Message posted successfully!' + (newMessage.send_email ? ' Emails sent to all agents.' : ''));
      setNewMessage({ title: '', content: '', send_email: true });
      setShowMessageModal(false);
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Load agent commission breakdown
  const loadAgentCommissions = async (agent) => {
    setSelectedAgentForBreakdown(agent);
    setLoadingCommissions(true);
    setExpandedStatementDates({}); // Reset expanded state
    try {
      const token = localStorage.getItem('token');
      const endpoint = businessUnit === 'insurance' 
        ? `/api/insurance/commissions/mga?agent_id=${agent.id}&year=${selectedYear}`
        : `/api/commissions?agent_id=${agent.id}&year=${selectedYear}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Handle different response formats
      const commissions = Array.isArray(data) ? data : (data.commissions || []);
      setAgentCommissions(commissions);
    } catch (err) {
      toast.error('Failed to load commission breakdown');
    } finally {
      setLoadingCommissions(false);
    }
  };

  // Group commissions by statement date
  const getCommissionsByStatementDate = () => {
    const grouped = {};
    agentCommissions.forEach(comm => {
      // Use statement_date or fallback to other date fields
      const dateKey = comm.statement_date || comm.paid_to_agent_date || comm.payment_date || comm.transaction_date || 'Unknown Date';
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          transactions: [],
          totalCommission: 0,
          totalBasis: 0
        };
      }
      grouped[dateKey].transactions.push(comm);
      grouped[dateKey].totalCommission += parseFloat(comm.agent_comm || comm.agent_commission || 0);
      grouped[dateKey].totalBasis += parseFloat(comm.basis_amount || comm.mortgage_loan || 0);
    });
    
    // Sort by date descending
    return Object.entries(grouped).sort((a, b) => {
      const dateA = new Date(a[0]);
      const dateB = new Date(b[0]);
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      return dateB - dateA;
    });
  };

  // Toggle expansion for a statement date
  const toggleStatementDateExpansion = (dateKey) => {
    setExpandedStatementDates(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  // Navigate to source transaction
  const navigateToSource = (commission) => {
    const baseRoute = businessUnit === 'insurance' ? '/insurance' : '/commissions';
    // Include transaction ID in URL for highlighting/filtering
    const transactionId = commission.id || commission._id;
    window.open(`${baseRoute}?highlightId=${transactionId}`, '_blank');
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await messages.delete(messageId);
      toast.success('Message deleted');
      loadMessages();
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await messages.markRead(messageId);
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const metrics = isAgent ? [
    {
      title: 'TOTAL COMMISSIONS',
      value: formatCurrency(stats?.agent_earnings || 0),
      icon: DollarSign,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'TOTAL EXPENSES',
      value: formatCurrency(stats?.total_expenses || 0),
      icon: Receipt,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-amber-500 to-orange-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'NET EARNINGS',
      value: formatCurrency(stats?.net_earnings || 0),
      icon: TrendingUp,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
  ] : businessUnit === 'insurance' ? [
    // Insurance Division metrics
    {
      title: 'TOTAL COMMISSIONS',
      value: formatCurrency(stats?.total_commissions || 0),
      icon: DollarSign,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'AGENT EARNINGS',
      value: formatCurrency(stats?.agent_earnings || 0),
      icon: Award,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-green-500 to-emerald-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'BROKERAGE EARNINGS',
      value: formatCurrency(stats?.brokerage_earnings || 0),
      icon: Building2,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-slate-600 to-slate-800',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'ACTIVE AGENTS',
      value: stats?.total_agents || 0,
      icon: Users,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-violet-500 to-purple-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
  ] : [
    // Mortgage Division metrics
    {
      title: 'TOTAL COMMISSIONS',
      value: formatCurrency(stats?.total_commissions || 0),
      icon: DollarSign,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'MORTGAGE VOLUME',
      value: formatCompactCurrency(stats?.total_mortgage_volume || 0),
      icon: TrendingUp,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'ACTIVE AGENTS',
      value: stats?.total_agents || 0,
      icon: Users,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-violet-500 to-purple-600',
      iconBg: 'bg-white/20',
      clickable: false
    },
    {
      title: 'PENDING PAYMENTS',
      value: stats?.pending_payments || 0,
      icon: Clock,
      color: 'text-white',
      bg: 'bg-gradient-to-br from-amber-500 to-orange-600',
      iconBg: 'bg-white/20',
      clickable: true,
      onClick: handleOpenPendingModal
    },
  ];

  return (
    <div className="space-y-6 lg:space-y-8 animate-fadeIn" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${businessUnit === 'insurance' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
            <BuIcon className={`w-5 h-5 ${businessUnit === 'insurance' ? 'text-blue-600' : 'text-emerald-600'}`} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight truncate" style={{ fontFamily: 'Manrope' }}>
              {isAgent ? 'My Dashboard' : 'Dashboard'}
            </h1>
            <p className="text-sm text-slate-500 truncate">{buLabel} Division - YTD {selectedYear}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Year:</span>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 touch-manipulation" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {metrics.map((metric, index) => (
          <Card
            key={index}
            className={`metric-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${metric.bg} ${metric.clickable ? 'cursor-pointer hover:scale-105 active:scale-100' : 'hover:scale-[1.02]'} touch-manipulation`}
            data-testid={`metric-${metric.title.toLowerCase().replace(/\s/g, '-')}`}
            onClick={metric.clickable ? metric.onClick : undefined}
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white/80 tracking-wider mb-1 sm:mb-2">
                    {metric.title}
                    {metric.clickable && <span className="ml-1 text-xs text-white/60 hidden sm:inline">(Click to view)</span>}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-white font-mono truncate">
                    {metric.value}
                  </p>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${metric.iconBg} flex items-center justify-center backdrop-blur-sm flex-shrink-0 ml-2`}>
                  <metric.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Earnings Summary - Hidden for agents, different for mortgage vs insurance */}
      {!isAgent && businessUnit === 'mortgage' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-green-600 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -mr-12 -mt-12 sm:-mr-16 sm:-mt-16"></div>
            <CardContent className="p-4 sm:p-6 relative z-10">
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white/80 tracking-wider">AGENT EARNINGS (YTD {selectedYear})</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white font-mono truncate">
                    {formatCurrency(stats?.agent_earnings || 0)}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full"
                  style={{
                    width: `${stats?.total_commissions ? (stats.agent_earnings / stats.total_commissions) * 100 : 0}%`
                  }}
                />
              </div>
              <p className="text-xs text-white/80 mt-2">
                {stats?.total_commissions ? ((stats.agent_earnings / stats.total_commissions) * 100).toFixed(1) : 0}% of total commissions
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/5 rounded-full -mr-12 -mt-12 sm:-mr-16 sm:-mt-16"></div>
            <CardContent className="p-4 sm:p-6 relative z-10">
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white/80 tracking-wider">BROKERAGE EARNINGS (YTD {selectedYear})</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white font-mono truncate">
                    {formatCurrency(stats?.brokerage_earnings || 0)}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full"
                  style={{
                    width: `${stats?.total_commissions ? (stats.brokerage_earnings / stats.total_commissions) * 100 : 0}%`
                  }}
                />
              </div>
              <p className="text-xs text-white/80 mt-2">
                {stats?.total_commissions ? ((stats.brokerage_earnings / stats.total_commissions) * 100).toFixed(1) : 0}% of total commissions
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -mr-12 -mt-12 sm:-mr-16 sm:-mt-16"></div>
            <CardContent className="p-4 sm:p-6 relative z-10">
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white/80 tracking-wider">ROYALTY + GST (YTD {selectedYear})</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white font-mono truncate">
                    {formatCurrency(stats?.royalty_plus_gst || 0)}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full"
                  style={{
                    width: `${stats?.total_commissions ? (stats.royalty_plus_gst / stats.total_commissions) * 100 : 0}%`
                  }}
                />
              </div>
              <p className="text-xs text-white/80 mt-2">
                {stats?.total_commissions ? ((stats.royalty_plus_gst / stats.total_commissions) * 100).toFixed(1) : 0}% of total commissions
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Section - Insurance Only */}
      {!isAgent && businessUnit === 'insurance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Monthly Commission Trend Chart */}
          {stats?.monthly_data && stats.monthly_data.length > 0 && (
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                  Monthly Commission Trend ({selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.monthly_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 10 }}
                      tickFormatter={(val) => {
                        const [, month] = val.split('-');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return monthNames[parseInt(month, 10) - 1] || val;
                      }}
                    />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `$${(val/1000).toFixed(0)}K`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(val) => {
                        const [year, month] = val.split('-');
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                        return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
                      }}
                    />
                    <Bar dataKey="commission" fill="#10B981" name="Total Commission" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Carrier Distribution Chart */}
          {stats?.lender_distribution && stats.lender_distribution.length > 0 && (
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-violet-500 to-purple-500"></div>
                  Carrier Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={stats.lender_distribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="total"
                        nameKey="carrier"
                      >
                        {stats.lender_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Horizontal Legend with wrapping */}
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 px-4">
                    {stats.lender_distribution.map((entry, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <div 
                          className="w-3 h-3 rounded-sm flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span 
                          className="text-sm font-medium"
                          style={{ color: COLORS[index % COLORS.length] }}
                        >
                          {entry.carrier}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Top Performing Agents - Insurance Only */}
      {!isAgent && businessUnit === 'insurance' && stats?.top_agents && stats.top_agents.length > 0 && (
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500"></div>
                Top Performing Agents ({selectedYear})
              </CardTitle>
              <span className="text-sm text-slate-500">{stats.top_agents.length} agents</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(showAllAgents ? stats.top_agents : stats.top_agents.slice(0, 5)).map((agent, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500' :
                      index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' :
                      index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                      'bg-gradient-to-br from-slate-300 to-slate-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{agent.name}</p>
                      <p className="text-xs text-slate-500">{agent.transaction_count || 0} transactions</p>
                    </div>
                  </div>
                  <div 
                    className="text-right cursor-pointer hover:bg-emerald-50 p-2 rounded-lg transition-colors"
                    onClick={() => loadAgentCommissions(agent)}
                    title="Click to view commission breakdown"
                  >
                    <p className="font-mono font-bold text-emerald-600 hover:text-emerald-700">{formatCurrency(agent.commission)}</p>
                    <p className="text-xs text-slate-500">Agent Earnings →</p>
                  </div>
                </div>
              ))}
            </div>
            {stats.top_agents.length > 5 && (
              <Button
                variant="ghost"
                className="w-full mt-4 text-slate-600 hover:text-slate-900"
                onClick={() => setShowAllAgents(!showAllAgents)}
              >
                {showAllAgents ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show All {stats.top_agents.length} Agents
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Earnings Breakdown - Admin Only */}
      {!isAgent && stats?.monthly_data && stats.monthly_data.length > 0 && (
        <Card className="border-0 shadow-lg bg-white" data-testid="monthly-earnings">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              Monthly Earnings ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100">
                    <TableHead className="font-semibold">Month</TableHead>
                    <TableHead className="text-right font-semibold">Total Commission</TableHead>
                    <TableHead className="text-right font-semibold text-emerald-700">Agent Earnings</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Brokerage Earnings</TableHead>
                    {businessUnit === 'mortgage' && (
                      <TableHead className="text-right font-semibold text-blue-700">Mortgage Volume</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.monthly_data.map((m, index) => {
                    // Parse month string (YYYY-MM) without timezone issues
                    const [year, month] = m.month.split('-');
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                       'July', 'August', 'September', 'October', 'November', 'December'];
                    const monthName = `${monthNames[parseInt(month, 10) - 1]} ${year}`;
                    return (
                      <TableRow key={index} className="hover:bg-slate-50 transition-colors">
                        <TableCell className="font-medium">{monthName}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.commission)}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-600 font-medium">{formatCurrency(m.agent_earnings || 0)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600 font-medium">{formatCurrency(m.brokerage_earnings || 0)}</TableCell>
                        {businessUnit === 'mortgage' && (
                          <TableCell className="text-right font-mono text-blue-600">{formatCurrency(m.volume)}</TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year-over-Year Comparison - Admin Only */}
      {!isAgent && stats?.yearly_data && stats.yearly_data.length > 0 && (
        <Card className="border-0 shadow-lg bg-white" data-testid="yearly-comparison">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
              Year-over-Year Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100">
                    <TableHead className="font-semibold">Year</TableHead>
                    <TableHead className="text-right font-semibold">Total Commissions</TableHead>
                    <TableHead className="text-right font-semibold text-emerald-700">Agent Earnings</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Brokerage Earnings</TableHead>
                    {businessUnit === 'mortgage' && (
                      <TableHead className="text-right font-semibold text-blue-700">Mortgage Volume</TableHead>
                    )}
                    <TableHead className="text-center font-semibold">Transactions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.yearly_data.map((y, index) => (
                    <TableRow key={index} className={`hover:bg-slate-50 transition-colors ${y.year === String(selectedYear) ? 'bg-gradient-to-r from-emerald-50 to-teal-50' : ''}`}>
                      <TableCell className="font-semibold">
                        {y.year}
                        {y.year === String(selectedYear) && (
                          <span className="ml-2 text-xs bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-2 py-0.5 rounded-full">Selected</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">{formatCurrency(y.total_commissions)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600 font-semibold">{formatCurrency(y.agent_earnings)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-600 font-semibold">{formatCurrency(y.brokerage_earnings)}</TableCell>
                      {businessUnit === 'mortgage' && (
                        <TableCell className="text-right font-mono text-blue-600">{formatCurrency(y.mortgage_volume)}</TableCell>
                      )}
                      <TableCell className="text-center">
                        <span className="bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 text-sm px-3 py-1 rounded-full font-mono font-semibold">
                          {y.transaction_count}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts - Mortgage Division Only (Insurance has its own charts above) */}
      {!isAgent && businessUnit === 'mortgage' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Commissions Chart */}
        <Card className="lg:col-span-2 border-0 shadow-lg bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"></div>
              Monthly Commission Trend ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.monthly_data || []}>
                  <defs>
                    <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={1}/>
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return monthNames[parseInt(month, 10) - 1];
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => formatCompactCurrency(value)}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), 'Commission']}
                    labelFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                         'July', 'August', 'September', 'October', 'November', 'December'];
                      return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
                    }}
                    contentStyle={{
                      backgroundColor: '#0F172A',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }}
                  />
                  <Bar dataKey="commission" fill="url(#colorCommission)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Lender Distribution - Mortgage Only */}
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-violet-500 to-purple-500"></div>
              Lender Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats?.lender_distribution || []}
                    dataKey="total"
                    nameKey="lender"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    label={({ lender, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {(stats?.lender_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: '#0F172A',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }}
                  />
                  <Legend
                    formatter={(value) => value}
                    wrapperStyle={{ fontSize: '12px', color: '#475569' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Top Agents - Admin Only, Mortgage Division Only (Insurance has its own above) */}
      {!isAgent && businessUnit === 'mortgage' && (
        <Card className="border-0 shadow-lg bg-white" data-testid="top-agents">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500"></div>
                Top Performing Agents ({selectedYear})
              </CardTitle>
              <span className="text-sm text-slate-500">{(stats?.top_agents || []).length} agents</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(showAllAgents ? (stats?.top_agents || []) : (stats?.top_agents || []).slice(0, 5)).map((agent, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg ${
                    index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 
                    index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-600' : 
                    index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' : 
                    'bg-gradient-to-br from-slate-300 to-slate-400'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{agent.name}</p>
                    <div className="mt-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          index === 0 ? 'bg-gradient-to-r from-amber-400 to-amber-600' :
                          index === 1 ? 'bg-gradient-to-r from-slate-400 to-slate-600' :
                          index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                          'bg-gradient-to-r from-emerald-400 to-emerald-600'
                        }`}
                        style={{
                          width: `${stats.top_agents[0]?.commission ? (agent.commission / stats.top_agents[0].commission) * 100 : 0}%`
                        }}
                      />
                    </div>
                  </div>
                  <div 
                    className="cursor-pointer hover:bg-slate-50 rounded-lg px-3 py-1 transition-colors"
                    onClick={() => loadAgentCommissions(agent)}
                    title="Click to view commission breakdown"
                  >
                    <p className="font-mono text-sm font-bold text-slate-900">
                      {formatCurrency(agent.commission)}
                    </p>
                    <p className="text-xs text-slate-500 text-right">View →</p>
                  </div>
                </div>
              ))}
            </div>
            {(stats?.top_agents || []).length > 5 && (
              <Button
                variant="ghost"
                className="w-full mt-4 text-slate-600 hover:text-slate-900"
                onClick={() => setShowAllAgents(!showAllAgents)}
              >
                {showAllAgents ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show All {(stats?.top_agents || []).length} Agents
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Message Board */}
      <Card className="border-0 shadow-lg bg-white" data-testid="message-board">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"></div>
            {isAgent ? 'Announcements' : 'Message Board'}
          </CardTitle>
          {!isAgent && (
            <Button
              onClick={() => setShowMessageModal(true)}
              size="sm"
              className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 shadow-lg"
              data-testid="post-message-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Post Message
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {messageList.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No messages yet</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {messageList.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isAgent && !msg.is_read 
                      ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md' 
                      : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                  }`}
                  onClick={() => isAgent && !msg.is_read && handleMarkMessageRead(msg.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {isAgent && !msg.is_read && (
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        )}
                        <h4 className="font-semibold text-slate-900">{msg.title}</h4>
                      </div>
                      <p className="text-slate-600 text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        Posted by {msg.created_by_name} • {formatDate(msg.created_at)}
                      </p>
                    </div>
                    {!isAgent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Post Message Modal - Admin Only */}
      {!isAgent && (
        <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
          <DialogContent className="max-w-lg" data-testid="post-message-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Send className="w-5 h-5 text-slate-600" />
                Post New Message
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="msg-title">Title</Label>
                <Input
                  id="msg-title"
                  placeholder="Message title"
                  value={newMessage.title}
                  onChange={(e) => setNewMessage({ ...newMessage, title: e.target.value })}
                  data-testid="message-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="msg-content">Message</Label>
                <Textarea
                  id="msg-content"
                  placeholder="Write your message here..."
                  rows={5}
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  data-testid="message-content-input"
                />
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  id="send-email"
                  checked={newMessage.send_email}
                  onCheckedChange={(checked) => setNewMessage({ ...newMessage, send_email: checked })}
                />
                <div className="flex-1">
                  <Label htmlFor="send-email" className="cursor-pointer">
                    Send email notification to all agents
                  </Label>
                  <p className="text-xs text-slate-500">Agents will receive this message via email</p>
                </div>
                <Bell className="w-5 h-5 text-slate-400" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMessageModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePostMessage}
                disabled={sendingMessage}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Post Message
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Pending Payments Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="pending-payments-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Pending Payments ({pendingPayments.length})
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {loadingPending ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : pendingPayments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-2" />
                <p>All payments have been processed!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-center">Statement #</TableHead>
                    <TableHead className="text-center">Date</TableHead>
                    <TableHead className="text-center">Agent</TableHead>
                    <TableHead className="text-center">Description</TableHead>
                    <TableHead className="text-center">Gross Commission</TableHead>
                    <TableHead className="text-center">Agent Commission</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingPayments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`pending-payment-${payment.id}`}>
                      <TableCell className="font-mono text-sm text-center">{payment.dlc_statement_no}</TableCell>
                      <TableCell className="font-mono text-sm text-center">{formatDate(payment.statement_date)}</TableCell>
                      <TableCell className="font-medium text-center">{payment.agent_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-center" title={payment.description}>
                        {payment.description}
                      </TableCell>
                      <TableCell className="font-mono text-right text-emerald-600">
                        {formatCurrency(payment.gross_commission)}
                      </TableCell>
                      <TableCell className="font-mono text-right text-emerald-600 font-medium">
                        {formatCurrency(payment.agent_commission)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          onClick={() => handleMarkAsPaid(payment)}
                          disabled={markingPaid === payment.id}
                          data-testid={`mark-paid-${payment.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {markingPaid === payment.id ? (
                            <span className="flex items-center gap-1">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              Processing...
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Check className="w-4 h-4" /> Mark as Paid
                            </span>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Commission Breakdown Modal */}
      <Dialog open={!!selectedAgentForBreakdown} onOpenChange={() => setSelectedAgentForBreakdown(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="commission-breakdown-modal">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-emerald-600" />
                Commission Breakdown - {selectedAgentForBreakdown?.name}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  const totalEarnings = formatCurrency(selectedAgentForBreakdown?.commission || 0);
                  const groupedData = getCommissionsByStatementDate();
                  
                  let content = '';
                  groupedData.forEach(([dateKey, data]) => {
                    content += `
                      <div class="statement-group">
                        <div class="statement-header">
                          <strong>Statement Date: ${formatDate(dateKey)}</strong>
                          <span class="statement-total">${formatCurrency(data.totalCommission)}</span>
                        </div>
                        <table>
                          <thead>
                            <tr>
                              <th>${businessUnit === 'insurance' ? 'Carrier' : 'Lender'}</th>
                              <th>${businessUnit === 'insurance' ? 'Insured' : 'Client'}</th>
                              <th style="text-align: right;">Basis Amount</th>
                              <th style="text-align: right;">Agent Commission</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${data.transactions.map(comm => `
                              <tr>
                                <td>${comm.carrier || comm.lender_name || '-'}</td>
                                <td>${comm.insured_name || comm.client_name || '-'}</td>
                                <td style="text-align: right;">${formatCurrency(comm.basis_amount || comm.mortgage_loan || 0)}</td>
                                <td style="text-align: right; color: #059669; font-weight: 600;">${formatCurrency(comm.agent_comm || comm.agent_commission || 0)}</td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                      </div>
                    `;
                  });
                  
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Commission Breakdown - ${selectedAgentForBreakdown?.name}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1 { color: #1e293b; margin-bottom: 5px; }
                          h2 { color: #64748b; font-weight: normal; margin-top: 0; }
                          .summary { display: flex; gap: 40px; margin: 20px 0; padding: 15px; background: #f8fafc; border-radius: 8px; }
                          .summary-item { }
                          .summary-label { font-size: 12px; color: #64748b; }
                          .summary-value { font-size: 20px; font-weight: bold; }
                          .total { color: #059669; }
                          .statement-group { margin-bottom: 24px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
                          .statement-header { display: flex; justify-content: space-between; padding: 12px 16px; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; }
                          .statement-total { color: #059669; font-weight: 600; }
                          table { width: 100%; border-collapse: collapse; }
                          th { background: #f8fafc; padding: 8px 12px; text-align: left; font-weight: 600; font-size: 12px; color: #64748b; }
                          td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
                          @media print { body { padding: 0; } .statement-group { break-inside: avoid; } }
                        </style>
                      </head>
                      <body>
                        <h1>Commission Breakdown</h1>
                        <h2>${selectedAgentForBreakdown?.name} - ${selectedYear}</h2>
                        <div class="summary">
                          <div class="summary-item">
                            <div class="summary-label">Total Earnings</div>
                            <div class="summary-value total">${totalEarnings}</div>
                          </div>
                          <div class="summary-item">
                            <div class="summary-label">Statement Dates</div>
                            <div class="summary-value">${groupedData.length}</div>
                          </div>
                          <div class="summary-item">
                            <div class="summary-label">Total Transactions</div>
                            <div class="summary-value">${agentCommissions.length}</div>
                          </div>
                        </div>
                        ${content}
                        <script>window.onload = function() { window.print(); }</script>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                }}
                className="flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print All
              </Button>
            </div>
          </DialogHeader>
          
          {loadingCommissions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Total Earnings</p>
                  <p className="text-xl font-bold text-emerald-600 font-mono">
                    {formatCurrency(selectedAgentForBreakdown?.commission || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Statement Dates</p>
                  <p className="text-xl font-bold text-slate-900">
                    {getCommissionsByStatementDate().length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Transactions</p>
                  <p className="text-xl font-bold text-slate-900">{agentCommissions.length}</p>
                </div>
              </div>

              {/* Grouped by Statement Date */}
              {agentCommissions.length > 0 ? (
                <div className="space-y-3">
                  {getCommissionsByStatementDate().map(([dateKey, data]) => (
                    <div key={dateKey} className="border rounded-lg overflow-hidden">
                      {/* Statement Date Header - Click to expand */}
                      <div
                        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleStatementDateExpansion(dateKey)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedStatementDates[dateKey] ? (
                            <ChevronUp className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          )}
                          <div>
                            <p className="font-semibold text-slate-900">
                              Statement Date: {formatDate(dateKey)}
                            </p>
                            <p className="text-sm text-slate-500">
                              {data.transactions.length} transaction{data.transactions.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-emerald-600">
                            {formatCurrency(data.totalCommission)}
                          </p>
                          <p className="text-xs text-slate-500">Agent Commission</p>
                        </div>
                      </div>
                      
                      {/* Expanded Transaction Details */}
                      {expandedStatementDates[dateKey] && (
                        <div className="border-t">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50/50">
                                <TableHead className="text-xs">{businessUnit === 'insurance' ? 'Carrier' : 'Lender'}</TableHead>
                                <TableHead className="text-xs">{businessUnit === 'insurance' ? 'Insured' : 'Client'}</TableHead>
                                <TableHead className="text-xs text-right">Basis Amount</TableHead>
                                <TableHead className="text-xs text-right">Agent Commission</TableHead>
                                <TableHead className="text-xs text-center w-20">Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {data.transactions.map((comm, idx) => (
                                <TableRow key={idx} className="hover:bg-blue-50/50 transition-colors">
                                  <TableCell className="font-medium text-sm">
                                    {comm.carrier || comm.lender_name || '-'}
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-600">
                                    {comm.insured_name || comm.client_name || '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-sm">
                                    {formatCurrency(comm.basis_amount || comm.mortgage_loan || 0)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-emerald-600 font-semibold text-sm">
                                    {formatCurrency(comm.agent_comm || comm.agent_commission || 0)}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigateToSource(comm);
                                      }}
                                      title="View/Edit source transaction"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No commissions found for {selectedYear}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
